import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';

/**
 * PWA 커스텀 설치 배너 (모바일 전용, Footer 내부 인라인 배치).
 *
 *  - 노출 조건: 모바일 + 미설치(standalone 아님)
 *  - 첫 노출 지연 없음 — beforeinstallprompt 수신 즉시 표시
 *  - dismiss 기록 없음 — 매번 노출
 *  - 설치 버튼 → prompt() 실행 → 설치/거절 모두 컴포넌트 정리
 *  - appinstalled / display-mode standalone → 즉시 숨김
 *
 * iOS Safari 는 beforeinstallprompt 미지원 → 자동 노출되지 않음.
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone) return true;
  return false;
}

export function PWAInstallBanner() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isMobileDevice()) return;
    if (isStandalone()) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      const evt = e as BeforeInstallPromptEvent;
      setDeferred(evt);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!visible || !deferred) return null;

  const handleInstall = async () => {
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch {
      /* prompt() 가 이미 호출됐거나 user gesture 외에서 호출된 경우 — 조용히 무시 */
    } finally {
      setVisible(false);
      setDeferred(null);
    }
  };

  return (
    <div
      role="region"
      aria-label="앱 설치 안내"
      // Footer 내부 인라인 — 투자 고지 문구 바로 아래. 모바일만 노출.
      className="mx-3 mb-3 mt-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-sm md:hidden"
    >
      <div className="flex items-center gap-3">
        <img
          src="/app_icon_192.png"
          alt="시세공간"
          width={40}
          height={40}
          className="h-10 w-10 shrink-0 rounded-xl"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-[var(--color-text)]">
            시세공간 앱으로 설치하기
          </div>
          <div className="mt-0.5 truncate text-xs text-[var(--color-text-sub)]">
            홈화면에서 더 빠르게 시세를 확인하세요
          </div>
        </div>
        <button
          type="button"
          onClick={handleInstall}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--color-primary)] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:brightness-110 active:brightness-95"
        >
          <Download className="h-3.5 w-3.5" />
          설치
        </button>
      </div>
    </div>
  );
}
